// src/features/mcbimItems/hooks/usePrefetchMcBimItemsPerIfcClass.ts
import { useMemo, useEffect } from "react";
import { openapi, PostMcbimItemsApiArg } from "../../openapi";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { IfcClassesEnum } from "@multiconsult-group/multiplacer-core";
import { selectProjectNo } from "../globalSelectors";

const STREET_VIEW_CONTEXT_INSTANCE_CLASSES = [
  IfcClassesEnum.IFCWALL,
  IfcClassesEnum.IFCWALLSTANDARDCASE,
  "IfcSlab",
] as const;

// Norsk (Bokmål): Hjelpefunksjon for stabile, sorterte "aktive" nøkler
const getActiveKeysSorted = (map: Partial<Record<string, boolean>>): string[] => {
  const active = Object.entries(map ?? {})
    .filter(([, isOn]) => isOn === true)
    .map(([k]) => k);

  active.sort((a, b) => String(a).localeCompare(String(b)));
  return active;
};

export const usePrefetchMcBimItemsPerIfcClass = (): void => {
  const dispatch = useAppDispatch();

  const projectNo = useAppSelector(selectProjectNo);
  const floorAndJob = useAppSelector((s) => s.design.floorAndJob);

  // Norsk (Bokmål): Ny mode i redux: "INSTANCE_CLASS" | "DISCIPLINE"
  const mcbimItemsFilterMode = useAppSelector((s) => s.design.mcbimItemsFilterMode);

  // Eksisterende
  const ifcClassesToDisplay = useAppSelector((s) => s.design.ifcClassesToDisplay);

  // Ny
  const disciplinesToDisplay = useAppSelector((s) => s.design.disciplinesToDisplay);

  const buildingStoryName = floorAndJob?.floorName ?? null;

  const activeClassesSorted = useMemo(() => {
    return ifcClassesToDisplay.map(entry => entry.ifcClass as IfcClassesEnum).sort();
  }, [ifcClassesToDisplay]);

  const streetViewContextClassesSorted = useMemo(
    () =>
      Array.from(new Set(STREET_VIEW_CONTEXT_INSTANCE_CLASSES))
        .map((k) => k as IfcClassesEnum)
        .sort((a, b) => String(a).localeCompare(String(b))),
    []
  );

  const activeDisciplinesSorted = useMemo(() => {
    return getActiveKeysSorted(disciplinesToDisplay);
  }, [disciplinesToDisplay]);

  useEffect(() => {
    if (!projectNo || !buildingStoryName) return;

    const classesToPrefetch =
      mcbimItemsFilterMode === "INSTANCE_CLASS"
        ? Array.from(new Set([...activeClassesSorted, ...streetViewContextClassesSorted]))
        : streetViewContextClassesSorted;

    const classSubs = classesToPrefetch.map((instanceClass) => {
      const args: PostMcbimItemsApiArg = {
        body: {
          projectNo,
          buildingStoryName,
          instanceClasses: [instanceClass],
        },
      };

      return dispatch(
        openapi.endpoints.postMcbimItems.initiate(args, {
          subscribe: true,
          forceRefetch: false,
        })
      );
    });

    // Norsk (Bokmål): Prefetch per valgt IFC-klasse (samme som før)
    if (mcbimItemsFilterMode === "INSTANCE_CLASS") {
      if (classesToPrefetch.length === 0) return;

      return () => {
        classSubs.forEach((p) => p.unsubscribe());
      };
    }

    // Norsk (Bokmål): Prefetch per valgt disiplin
    if (mcbimItemsFilterMode === "DISCIPLINE") {
      if (activeDisciplinesSorted.length === 0) {
        return () => {
          classSubs.forEach((p) => p.unsubscribe());
        };
      }

      const disciplineSubs = activeDisciplinesSorted.map((discipline) => {
        const args: PostMcbimItemsApiArg = {
          body: {
            projectNo,
            buildingStoryName,
            disciplines: [discipline], // ✅ filtrer per disiplin
          },
        };

        return dispatch(
          openapi.endpoints.postMcbimItems.initiate(args, {
            subscribe: true,
            forceRefetch: false,
          })
        );
      });

      return () => {
        classSubs.forEach((p) => p.unsubscribe());
        disciplineSubs.forEach((p) => p.unsubscribe());
      };
    }

    return () => {
      classSubs.forEach((p) => p.unsubscribe());
    };
  }, [
    dispatch,
    projectNo,
    buildingStoryName,
    mcbimItemsFilterMode,
    activeClassesSorted,
    activeDisciplinesSorted,
    streetViewContextClassesSorted,
  ]);
};
